import { STATUS_APPROVED, STATUS_REVIEW, STATUS_CONFLICT, STATUS_DRAFT, STATUS_REJECTED } from '../constants/employeeStatus';
import { TYPE_TEXT, TYPE_DATE, TYPE_NUMBER, TYPE_BOOLEAN, TYPE_DECIMAL } from '../constants/types';
import { FIELD_EMPLOYEE, FIELD_USER, FIELD_ENUM } from '../constants/fields';
import { RENDER_COUNTRY } from '../constants/renderComponents';
import {
  CURRENCY_TABLE,
  FUNCTION_LEVEL_TABLE,
  JOB_TITLE_TABLE,
  REGULATION_TABLE,
  SECTOR_TABLE,
  STATION_TABLE,
  PERSON_TYPE_TABLE,
  STATUS_TABLE,
  EMPLOYER_TABLE,
  GENDER_TABLE,
  COUNTRY_TABLE,
  LAST_NAME_PREFERENCE_TABLE,
  UPDATE_REASON_TABLE,
  PHONE_COUNTRY_CODE_TABLE
} from '../constants/dataTableIds';
import { getFullName as getFullUserName } from './userUtilities';

/**
 * Gets the full name of an employee.
 * This should be used everywhere, except from sortable list views
 * (See `getSortableFullName` instead)
 *
 * @param {object} employee
 */
export const getFullName = (employee) => {
  if (!employee) return null;

  return [
    employee.title,
    employee.initials,
    (employee.lastName || employee.birthName),
    employee.postNominalTitle
  ].filter((v) => v).join(' ');
};

/**
 * Gets the full name in a sortable way. This should be used for sortable lists only.
 *
 * @param {object} employee
 */
export const getSortableFullName = (employee) => {
  if (!employee) return null;

  return [
    `${employee.lastName},`,
    employee.initials,
  ].filter((v) => v).join(' ');
};

/**
 * Gets the list of the coutries attached to the employee.
 *
 * @param {object} employee
 */
export const getCountryDetailsLabels = (employee) => {
  if (!employee || !employee.regulationSpecifics.country || employee.regulationSpecifics.country.length === 0) return null;

  return employee.countryDetails
    ? employee.countryDetails.reduce((acc, country, i) => (i !== 0 ? `${acc}, ${country.Land}` : `${country.Land}`), '')
    : '';
};

/**
 * Gets the employee's station if it exists
 *
 * @param {object} employee
 */
export const getStation = (employee) => employee && employee.stationDetails && employee.stationDetails.Omschrijving;

export const getStatusColor = (status) => {
  switch (status) {
    case STATUS_CONFLICT:
    case STATUS_REVIEW: {
      return 'primary';
    }
    case STATUS_APPROVED: {
      return 'secondary';
    }
    case STATUS_DRAFT:
    default: {
      return 'lightgray';
    }
  }
};

/**
 * Create a clean version based on the previous version
 * by destructuring every property we don't need from an employee
 */
export const newVersion = (baseVersion) => {
  // eslint-disable-next-line no-unused-vars
  const { _id, _assignee, _approvedAt, _approvedBy, _createdAt,
  // eslint-disable-next-line no-unused-vars
    _createdBy, _rejectedBy, _rejectReason, _status, _startDate,
  // eslint-disable-next-line no-unused-vars
    _updatedAt, _updatedBy, comments,
    ...cleanVersion
  } = baseVersion;

  return cleanVersion;
};

export const employeeListColumns = [
  {
    value: 'fullName',
    type: TYPE_TEXT,
    label: 'filter.full_name',
    getCellValue: (row) => getSortableFullName(row),
    defaultVisible: true
  },
  {
    value: 'station',
    type: TYPE_TEXT,
    label: 'filter.station',
    reference: STATION_TABLE,
    labelColumn: 'Omschrijving',
    valueColumn: 'Code',
    renderComponent: RENDER_COUNTRY,
    defaultVisible: true
  },
  {
    value: 'regulation',
    type: TYPE_TEXT,
    label: 'filter.regulation',
    reference: REGULATION_TABLE,
    labelColumn: 'Omschrijving',
    valueColumn: 'Code',
    defaultVisible: true
  },
  {
    value: '_createdBy',
    type: TYPE_TEXT,
    field: FIELD_USER,
    getCellValue: (row) => getFullUserName(row._createdByDetails),
    label: 'label.created_by',
  },
  {
    value: '_createdAt',
    type: TYPE_DATE,
    label: 'label.created_at'
  },
  {
    value: 'regulationSpecifics.salaryGross',
    type: TYPE_DECIMAL,
    label: 'filter.salary_gross',
  },
  {
    value: 'regulationSpecifics.salaryNet',
    type: TYPE_DECIMAL,
    label: 'filter.salary_net',
  },
  {
    value: 'regulationSpecifics.preMissionSalaryGross',
    type: TYPE_DECIMAL,
    label: 'filter.pre_mission_salary_gross',
  },
  {
    value: 'regulationSpecifics.preMissionSalaryNet',
    type: TYPE_DECIMAL,
    label: 'filter.pre_mission_salary_net',
  },
  {
    value: 'regulationSpecifics.seniorityDate',
    type: TYPE_DATE,
    label: 'filter.seniority_date',
  },
  {
    value: 'regulationSpecifics.preMissionSeniorityDate',
    type: TYPE_DATE,
    label: 'filter.pre_mission_seniority_date',
  },
  {
    value: 'manager',
    type: TYPE_TEXT,
    field: FIELD_USER,
    getCellValue: (row) => getFullUserName(row.managerDetails),
    label: 'filter.manager',
  },
  {
    value: 'paymentCode',
    type: TYPE_TEXT,
    label: 'filter.payment_code',
  },
  {
    value: '_updatedBy',
    type: TYPE_TEXT,
    field: FIELD_USER,
    getCellValue: (row) => getFullUserName(row._updatedByDetails),
    label: 'filter.updated_by',
  },
  {
    value: 'bziamNumber',
    type: TYPE_TEXT,
    label: 'filter.bziam_number',
  },
  {
    value: 'formationPositionNumber',
    type: TYPE_TEXT,
    label: 'filter.formation_position_number',
  },
  {
    value: 'userGroupTP',
    type: TYPE_TEXT,
    label: 'filter.user_group_tp'
  },
  {
    value: 'regulationSpecifics.contractHours',
    type: TYPE_NUMBER,
    label: 'filter.contract_hours',
  },
  {
    value: 'regulationSpecifics.preMissionContractHours',
    type: TYPE_DECIMAL,
    label: 'filter.pre_mission_contract_hours',
  },
  {
    value: 'regulationSpecifics.forcedMoveDate',
    type: TYPE_DATE,
    label: 'filter.forced_move_date',
  },
  {
    value: 'regulationSpecifics.car',
    type: TYPE_BOOLEAN,
    label: 'filter.car',
  },
  {
    value: 'regulationSpecifics.transportCar',
    type: TYPE_BOOLEAN,
    label: 'filter.transportCar',
  },
  {
    value: 'regulationSpecifics.residence',
    type: TYPE_BOOLEAN,
    label: 'filter.residence',
  },
  {
    value: 'regulationSpecifics.residenceFurnished',
    type: TYPE_BOOLEAN,
    label: 'filter.residence_furnished',
  },
  {
    value: 'documentationUrl',
    type: TYPE_TEXT,
    label: 'filter.documentation_url',
  },
  {
    value: 'email',
    type: TYPE_TEXT,
    label: 'filter.email',
  },
  {
    value: 'personalEmail',
    type: TYPE_TEXT,
    label: 'filter.personal_email',
  },
  {
    value: 'regulationSpecifics.functionLevel',
    type: TYPE_NUMBER,
    label: 'filter.function_level',
    reference: FUNCTION_LEVEL_TABLE,
    labelColumn: 'Beschrijving',
    valueColumn: 'Schaal'
  },
  {
    value: 'regulationSpecifics.functionLevelTemporary',
    type: TYPE_TEXT,
    label: 'filter.function_level_temporary',
    reference: FUNCTION_LEVEL_TABLE,
    labelColumn: 'Beschrijving',
    valueColumn: 'Schaal',
  },
  {
    value: 'regulationSpecifics.functionLevelPartner',
    type: TYPE_NUMBER,
    label: 'filter.function_level_partner',
    reference: FUNCTION_LEVEL_TABLE,
    labelColumn: 'Beschrijving',
    valueColumn: 'Schaal',
  },
  {
    value: 'regulationSpecifics.jobTitle',
    type: TYPE_TEXT,
    label: 'filter.job_title',
    reference: JOB_TITLE_TABLE,
    labelColumn: 'Omschrijving',
    valueColumn: 'Code',
    enrichmentField: 'jobTitleDetails',
  },
  {
    value: 'dateOfBirth',
    type: TYPE_DATE,
    label: 'filter.date_of_birth',
  },
  {
    value: 'partner.dateOfBirth',
    type: TYPE_DATE,
    label: 'filter.date_of_birth_partner',
  },
  {
    value: 'birthName',
    type: TYPE_TEXT,
    label: 'filter.birth_name',
  },
  {
    value: 'placeOfBirth',
    type: TYPE_TEXT,
    label: 'filter.place_of_birth',
  },
  {
    value: 'lastName',
    type: TYPE_TEXT,
    label: 'filter.last_name',
  },
  {
    value: 'generalName',
    type: TYPE_TEXT,
    label: 'filter.general_name',
  },
  {
    value: 'partner.birthName',
    type: TYPE_TEXT,
    label: 'filter.birth_name_partner',
  },
  {
    value: 'partner.generalName',
    type: TYPE_TEXT,
    label: 'filter.general_name_partner',
  },
  {
    value: 'regulationSpecifics.rentalAgreementEndDate',
    type: TYPE_DATE,
    label: 'filter.rental_agreement_end_date',
  },
  {
    value: 'regulationSpecifics.placementEndDate',
    type: TYPE_DATE,
    label: 'filter.placement_end_date',
  },
  {
    value: 'gender',
    type: TYPE_TEXT,
    label: 'filter.gender',
    reference: GENDER_TABLE,
    labelColumn: 'Omschrijving',
    valueColumn: 'Code',
  },
  {
    value: 'partner.gender',
    type: TYPE_TEXT,
    reference: GENDER_TABLE,
    labelColumn: 'Omschrijving',
    valueColumn: 'Code',
    label: 'filter.gender_partner',
  },
  {
    value: 'isPriorityTandem',
    type: TYPE_BOOLEAN,
    label: 'filter.priority_tandem',
    getCellValue: (row) => (row.priorityTandem)
  },
  {
    value: 'regulationSpecifics.domesticStaffSA',
    type: TYPE_BOOLEAN,
    label: 'filter.domestic_staff_SA',
  },
  {
    value: 'regulationSpecifics.monthlyRent',
    type: TYPE_DECIMAL,
    label: 'filter.monthly_rent',
  },
  {
    value: 'regulationSpecifics.dailyRent',
    type: TYPE_DECIMAL,
    label: 'filter.daily_rent',
  },
  {
    value: 'regulationSpecifics.rentalResidence',
    type: TYPE_BOOLEAN,
    label: 'filter.rental_residence',
  },
  {
    value: '_startDate',
    type: TYPE_DATE,
    label: 'filter.start_date',
  },
  {
    value: 'regulationSpecifics.localSalary',
    type: TYPE_DECIMAL,
    label: 'filter.local_salary',
  },
  {
    value: 'lastNamePreference',
    type: TYPE_TEXT,
    label: 'filter.last_name_preference',
    reference: LAST_NAME_PREFERENCE_TABLE,
    labelColumn: 'Omschrijving',
    valueColumn: 'Code',
  },
  {
    value: 'regulationSpecifics.personType',
    type: TYPE_TEXT,
    label: 'label.person_type',
    reference: PERSON_TYPE_TABLE,
    labelColumn: 'Omschrijving',
    valueColumn: 'Code',
    defaultVisible: true
  },
  {
    value: '_updatedAt',
    type: TYPE_DATE,
    label: 'filter.updated_at',
    defaultVisible: true
  },
  {
    value: 'comments',
    type: TYPE_TEXT,
    label: 'filter.comments',
  },
  {
    value: 'updateReason',
    type: TYPE_TEXT,
    label: 'filter.update_reason',
    reference: UPDATE_REASON_TABLE,
    labelColumn: 'Omschrijving',
    valueColumn: 'Code',
  },
  {
    value: 'regulationSpecifics.residenceEmbassy',
    type: TYPE_BOOLEAN,
    label: 'filter.residence_embassy',
  },
  {
    value: 'identityCode',
    type: TYPE_TEXT,
    label: 'filter.identity_code',
  },
  {
    value: 'regulationSpecifics.salaryScale',
    type: TYPE_NUMBER,
    label: 'filter.salary_scale',
    reference: FUNCTION_LEVEL_TABLE,
    labelColumn: 'Beschrijving',
    valueColumn: 'Schaal',
  },
  {
    value: 'regulationSpecifics.preMissionSalaryScale',
    type: TYPE_NUMBER,
    label: 'filter.pre_mission_salary_scale',
    reference: FUNCTION_LEVEL_TABLE,
    labelColumn: 'Beschrijving',
    valueColumn: 'Schaal',
  },
  {
    value: 'regulationSpecifics.sector',
    type: TYPE_TEXT,
    label: 'filter.sector',
    reference: SECTOR_TABLE,
    labelColumn: 'Omschrijving',
    valueColumn: 'Code',
    enrichmentField: 'sectorDetails',
  },
  {
    value: 'regulationSpecifics.preMissionSector',
    type: TYPE_TEXT,
    label: 'filter.pre_mission_sector',
    reference: SECTOR_TABLE,
    labelColumn: 'Omschrijving',
    valueColumn: 'Code',
  },
  {
    value: 'partner.station',
    type: TYPE_TEXT,
    label: 'filter.station_partner',
    reference: STATION_TABLE,
    labelColumn: 'Omschrijving',
    valueColumn: 'Code',
  },
  {
    value: 'regulationSpecifics.stationTemporary',
    type: TYPE_TEXT,
    label: 'filter.station_temporary',
    reference: STATION_TABLE,
    labelColumn: 'Omschrijving',
    valueColumn: 'Code',
  },
  {
    value: 'status',
    type: TYPE_TEXT,
    label: 'filter.status',
    reference: STATUS_TABLE,
    labelColumn: 'Omschrijving',
    valueColumn: 'Code',
  },
  {
    value: 'tandemId',
    type: TYPE_TEXT,
    field: FIELD_EMPLOYEE,
    getCellValue: (row) => getFullName(row.tandemIdDetails),
    label: 'filter.tandem_id',
  },
  {
    value: 'countryCode',
    type: TYPE_TEXT,
    label: 'filter.phone_number_country_code',
    reference: PHONE_COUNTRY_CODE_TABLE,
    labelColumn: 'Omschrijving',
    valueColumn: 'Code',
  },
  {
    value: 'areaCode',
    type: TYPE_TEXT,
    label: 'filter.phone_number_area_code',
  },
  {
    value: 'phoneNumber',
    type: TYPE_TEXT,
    label: 'filter.phone_number',
  },
  {
    value: 'postNominalTitle',
    type: TYPE_TEXT,
    label: 'filter.post_nominal_title',
  },
  {
    value: 'title',
    type: TYPE_TEXT,
    label: 'filter.title',
  },
  {
    value: '_assignee',
    type: TYPE_TEXT,
    label: 'filter.assignee',
    getCellValue: (row) => getFullUserName(row._assigneeDetails),
    field: FIELD_USER
  },
  {
    value: 'middleName',
    type: TYPE_TEXT,
    label: 'filter.middle_name',
  },
  {
    value: 'partner.middleName',
    type: TYPE_TEXT,
    label: 'filter.middle_name_partner',
  },
  {
    value: 'regulationSpecifics.rentCurrency',
    type: TYPE_TEXT,
    label: 'filter.rent_currency',
    reference: CURRENCY_TABLE,
    labelColumn: 'Omschrijving',
    valueColumn: 'Code'
  },
  {
    value: 'regulationSpecifics.dailyRentCurrency',
    type: TYPE_TEXT,
    label: 'filter.daily_rent_currency',
    reference: CURRENCY_TABLE,
    labelColumn: 'Omschrijving',
    valueColumn: 'Code'
  },
  {
    value: 'regulationSpecifics.currencyLocalSalary',
    type: TYPE_TEXT,
    label: 'filter.currency_local_salary',
    reference: CURRENCY_TABLE,
    labelColumn: 'Omschrijving',
    valueColumn: 'Code'
  },
  {
    value: '_status',
    field: FIELD_ENUM,
    type: TYPE_TEXT,
    label: 'filter.versie_status',
    enum: [
      { value: STATUS_DRAFT, label: 'status.draft' },
      { value: STATUS_REJECTED, label: 'status.rejected' },
      { value: STATUS_APPROVED, label: 'status.approved' },
      { value: STATUS_REVIEW, label: 'status.review' }
    ]
  },
  {
    value: 'firstNames',
    type: TYPE_TEXT,
    label: 'filter.first_names',
  },
  {
    value: 'initials',
    type: TYPE_TEXT,
    label: 'filter.initials',
  },
  {
    value: 'partner.initials',
    type: TYPE_TEXT,
    label: 'filter.initials_partner',
  },
  {
    value: 'partner.firstNames',
    type: TYPE_TEXT,
    label: 'filter.first_names_partner',
  },
  {
    value: 'regulationSpecifics.country',
    type: TYPE_TEXT,
    label: 'filter.country',
    getCellValue: (row) => getCountryDetailsLabels(row),
    reference: COUNTRY_TABLE,
    labelColumn: 'Land',
    valueColumn: 'Code',
    isArrayField: true,
    enrichmentField: 'countryDetails'
  },
  {
    value: 'regulationSpecifics.employer',
    type: TYPE_TEXT,
    label: 'filter.employer',
    reference: EMPLOYER_TABLE,
    labelColumn: 'Omschrijving',
    valueColumn: 'Code'
  }
]
// add getCellValue for nested properties
  .map((attr) => {
    if (attr.value.includes('.') && !attr.getCellValue) {
      const props = attr.value.split('.');
      attr.getCellValue = (e) => props.reduce((val, attr) => (val && val[attr] ? val[attr] : null), e);
    }
    return attr;
  });
